import type { SilenceSnapshot } from './useRecorder';
import { t } from '../../lib/i18n';

/**
 * Otomatik durdurma kuralları — saf fonksiyon, React'ten bağımsız ki
 * `npm run test:autostop` ile doğrulanabilsin (bkz. useAutoStop).
 *
 * Toplantının bittiğini doğrudan bilmenin yolu yok (Meet/Zoom/Teams'ten sinyal
 * gelmiyor), bu yüzden sese bakılıyor.
 */

const MIN = 60_000;

/** Online görüşme bitti: karşı taraf sustu ve siz de sustunuz. */
export const CALL_ENDED_SYSTEM_MS = 3 * MIN;
export const CALL_ENDED_MIC_MS = 1 * MIN;
/** Karşı taraf çok uzun süredir sessiz — mikrofonda oda gürültüsü olsa bile. */
export const REMOTE_LONG_SILENCE_MS = 15 * MIN;
/** Hiç ses yok — yüz yüze toplantılar için de geçerli. */
export const ALL_SILENT_MS = 5 * MIN;
/** Takvimdeki bitiş saatinden sonra daha kısa sessizlik yeterli. */
export const PAST_SCHEDULE_SYSTEM_MS = 1 * MIN;
export const PAST_SCHEDULE_ALL_MS = 2 * MIN;
/** Ne olursa olsun bu süreden uzun kayıt sorulur. */
export const MAX_DURATION_MS = 4 * 60 * MIN;
/** "Devam et" denirse üst sınır bu kadar uzar. */
export const MAX_DURATION_EXTEND_MS = 60 * MIN;
/**
 * Görüşme uygulaması mikrofonu bıraktıktan sonra karşı tarafın da bu kadar
 * sessiz olması beklenir — cihaz değiştirme gibi anlık bırakmalar sayılmasın.
 */
export const CALL_CLOSED_SYSTEM_MS = 15_000;

export type AutoStopReason =
  | 'call-closed'
  | 'call-ended'
  | 'remote-long-silence'
  | 'all-silent'
  | 'past-schedule'
  | 'max-duration';

/** Uyarıda gösterilen neden — seçili dilde, çizim anında üretilir. */
export function autoStopReasonText(reason: AutoStopReason): string {
  switch (reason) {
    case 'call-closed':
      return t('Görüşme uygulaması mikrofonu bıraktı; görüşme bitmiş görünüyor.', 'The call app released the microphone; the call seems to be over.');
    case 'call-ended':
      return t('Karşı taraftan 3 dakikadır ses gelmiyor.', 'No audio from the other side for 3 minutes.');
    case 'remote-long-silence':
      return t('Karşı taraftan 15 dakikadır ses gelmiyor.', 'No audio from the other side for 15 minutes.');
    case 'all-silent':
      return t('5 dakikadır hiç ses algılanmadı.', 'No sound detected for 5 minutes.');
    case 'past-schedule':
      return t('Takvimdeki toplantı saati bitti ve ses kesildi.', 'The scheduled meeting time is over and the audio has stopped.');
    case 'max-duration':
      return t('Kayıt uzun süredir devam ediyor (4 saati geçti).', 'The recording has been running for a long time (over 4 hours).');
  }
}

export interface AutoStopInput {
  now: number;
  /** Kaydın canlıya geçtiği an (epoch ms). */
  liveSince: number;
  /** Geçerli üst sınır — "devam et" ile uzayabilir. */
  maxDurationMs: number;
  isPaused: boolean;
  /** Son "devam et" anı; sessizlik bundan önceye uzanamaz. */
  snoozedAt: number;
  silence: SilenceSnapshot;
  /** Takvim etkinliğinin bitişi (epoch ms), yoksa null. */
  scheduledEndAt: number | null;
  /**
   * "Görüşme bitince kaydı durdur" açık mı. Kapalıyken yalnızca üst süre
   * sınırı sorulur — unutulan kayıt koruması kalsın.
   */
  callEndRules: boolean;
  /** Görüşme uygulaması mikrofonu bıraktığı an (Windows); bilinmiyorsa null. */
  callEndedAt: number | null;
}

/** Kayıt şu an bitirilmeli mi? Bitirilmeliyse nedeni, değilse null. */
export function evaluateAutoStop(input: AutoStopInput): AutoStopReason | null {
  const { now, silence } = input;

  if (now - input.liveSince >= input.maxDurationMs) return 'max-duration';
  if (!input.callEndRules) return null;
  // Duraklatılmış kayıt ses almıyor; sessizlik kuralları uygulanmaz.
  if (input.isPaused) return null;

  const sinceSnooze = now - input.snoozedAt;
  const mic = Math.min(silence.micSilentMs, sinceSnooze);
  const system = Math.min(silence.systemSilentMs, sinceSnooze);
  const all = Math.min(mic, system);

  // En doğrudan sinyal: görüşme uygulaması mikrofonu bıraktı ve karşı taraf sustu.
  // "Devam et"ten önceki bir bitiş sayılmaz.
  if (input.callEndedAt !== null && input.callEndedAt > input.snoozedAt && system >= CALL_CLOSED_SYSTEM_MS) {
    return 'call-closed';
  }

  const pastSchedule = input.scheduledEndAt !== null && now >= input.scheduledEndAt;
  if (pastSchedule) {
    if (silence.systemEverHeard && system >= PAST_SCHEDULE_SYSTEM_MS) return 'past-schedule';
    if (all >= PAST_SCHEDULE_ALL_MS) return 'past-schedule';
  }

  if (silence.systemEverHeard) {
    if (system >= CALL_ENDED_SYSTEM_MS && mic >= CALL_ENDED_MIC_MS) return 'call-ended';
    if (system >= REMOTE_LONG_SILENCE_MS) return 'remote-long-silence';
  }

  if (all >= ALL_SILENT_MS) return 'all-silent';
  return null;
}
